import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDoubleUpIcon,
  ChevronUpIcon,
  MinusIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { DIRECTUS_CHAT_SESSIONS_ENDPOINT, directusAPI } from '../../../api';
import {
  DEFAULT_PRIORITY_COUNTS,
  EMPTY_INBOX_SELECTED_FILTERS,
  INBOX_ITEMS_PER_PAGE,
  MAX_INBOX_SCAN_PAGES
} from '../constants/inboxConstants';
import { SORT_OPTION_KEYS } from '../constants/inboxLabels';
import { applyInboxSessionFilters, getEffectiveMessageCount } from '../utils/inboxSessionFilters';
import { computePriorityCounts } from '../utils/inboxPriorityCounts';

const normalizeFetchOptions = (arg = {}) => {
  if (arg === true) return { resetPage: true };
  return { resetPage: false, page: undefined, ...arg };
};

const getFirstUserMessagesBatch = async (sessionIds) => {
  if (!sessionIds.length) return {};

  try {
    const response = await directusAPI.get('/items/chat_messages', {
      params: {
        filter: {
          session_id: { _in: sessionIds },
          role: { _eq: 'user' }
        },
        fields: ['session_id', 'content', 'order', 'date_created'],
        limit: sessionIds.length * 3
      }
    });

    const grouped = {};
    (response.data.data || []).forEach((message) => {
      if (!grouped[message.session_id]) grouped[message.session_id] = [];
      grouped[message.session_id].push(message);
    });

    const result = {};
    Object.entries(grouped).forEach(([sessionId, messages]) => {
      messages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      result[sessionId] = messages[0]?.content || null;
    });
    return result;
  } catch (error) {
    process.env.NODE_ENV === 'development' && console.error('Error fetching first messages batch:', error);
    return {};
  }
};

const mapRawSessions = async (sessions) => {
  const sessionIds = sessions.map((session) => session.id);
  const firstMessagesBySession = await getFirstUserMessagesBatch(sessionIds);

  return sessions.map((session) => {
    const firstUserMessage = firstMessagesBySession[session.id] || null;
    const displayTitle = firstUserMessage || session.title || 'No title';

    return {
      id: session.id,
      title: displayTitle,
      firstMessage: firstUserMessage,
      messageCount: getEffectiveMessageCount(session),
      userCreated: session.user_created?.first_name && session.user_created?.last_name
        ? `${session.user_created.first_name} ${session.user_created.last_name}`
        : session.user_created?.email || 'Unknown user',
      dateCreated: new Date(session.date_created),
      dateUpdated: session.date_updated ? new Date(session.date_updated) : null,
      linkedWorkspace: session.linked_workspace,
      sourceType: session.source_type,
      sentiment: session.overall_sentiment,
      sentimentLabels: session.overall_sentiment_labels ? (() => {
        try {
          return JSON.parse(session.overall_sentiment_labels);
        } catch {
          return null;
        }
      })() : null,
      rawSentimentTopics: session.overall_sentiment_topics,
      rawSentimentLabels: session.overall_sentiment_labels,
      primaryIntent: session.primary_intent || null,
      intentFulfilled: session.intent_fulfilled || null,
      priority: session.priority_band || session.business_risk || null
    };
  });
};

const buildSessionFilterConditions = (workspaceIdNumber, { hideSingleMessage = false } = {}) => {
  const conditions = [
    {
      _or: [
        { linked_workspace: { _eq: workspaceIdNumber } },
        {
          _and: [
            { linked_workspace: { _null: true } },
            { user_created: { linked_workspace: { _eq: workspaceIdNumber } } }
          ]
        }
      ]
    },
    {
      _or: [
        { status: { _neq: 'archived' } },
        { status: { _null: true } }
      ]
    }
  ];

  if (hideSingleMessage) {
    conditions.push({ messages_count: { _gt: 1 } });
  }

  return conditions;
};

const useAssistantInbox = ({
  urlSessionId,
  token,
  workspaceId,
  updateURL,
  loadSpecificSession,
  loadedSessionIdRef,
  fetchSessionsListRef: externalFetchSessionsListRef,
  selectedSessionIdRef: externalSelectedSessionIdRef
}) => {
  const { t } = useTranslation();

  const [chatSessions, setChatSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('date_created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isInboxRefreshing, setIsInboxRefreshing] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(urlSessionId || null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [hideSingleMessage, setHideSingleMessage] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_INBOX_SELECTED_FILTERS);
  const [priorityCounts, setPriorityCounts] = useState(DEFAULT_PRIORITY_COUNTS);

  const sortDropdownRef = useRef(null);
  const bootstrappedWorkspaceRef = useRef(null);
  const internalSelectedSessionIdRef = useRef(selectedSessionId);
  const selectedSessionIdRef = externalSelectedSessionIdRef || internalSelectedSessionIdRef;
  const urlSessionIdRef = useRef(urlSessionId);
  const internalFetchSessionsListRef = useRef(async () => {});
  const fetchSessionsListRef = externalFetchSessionsListRef || internalFetchSessionsListRef;
  const bootstrapInProgressRef = useRef(false);
  const bootstrapSessionIdRef = useRef(null);
  const filtersSnapshotRef = useRef(null);
  const currentPageRef = useRef(1);
  const listRequestSeqRef = useRef(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / INBOX_ITEMS_PER_PAGE)),
    [totalCount]
  );

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    selectedSessionIdRef.current = selectedSessionId;
  }, [selectedSessionId, selectedSessionIdRef]);

  useEffect(() => {
    urlSessionIdRef.current = urlSessionId;
  }, [urlSessionId]);

  const sortOptions = useMemo(
    () => SORT_OPTION_KEYS.map((option) => ({ ...option, label: t(option.key) })),
    [t]
  );

  const priorityFilterOptions = useMemo(
    () => [
      { value: 'P0', label: t('assistant.priority.levels.P0.label'), Icon: ChevronDoubleUpIcon, color: 'text-red-600', dot: 'bg-red-500' },
      { value: 'P1', label: t('assistant.priority.levels.P1.label'), Icon: ChevronUpIcon, color: 'text-orange-600', dot: 'bg-orange-500' },
      { value: 'P2', label: t('assistant.priority.levels.P2.label'), Icon: MinusIcon, color: 'text-blue-500', dot: 'bg-blue-500' },
      { value: 'P3', label: t('assistant.priority.levels.P3.label'), Icon: ChevronDownIcon, color: 'text-emerald-600', dot: 'bg-emerald-500' }
    ],
    [t]
  );

  const visiblePriorityFilters = useMemo(
    () => priorityFilterOptions.filter(({ value }) => priorityCounts[value] > 0),
    [priorityFilterOptions, priorityCounts]
  );

  useEffect(() => {
    const availableValues = new Set(visiblePriorityFilters.map((item) => item.value));
    setSelectedFilters((prev) => {
      const filteredPriority = prev.priority.filter((value) => availableValues.has(value));
      if (filteredPriority.length === prev.priority.length) {
        return prev;
      }
      return { ...prev, priority: filteredPriority };
    });
  }, [visiblePriorityFilters]);

  const priorityFilterContext = useMemo(() => ({
    selectedFilters,
    hideSingleMessage
  }), [selectedFilters, hideSingleMessage]);

  const applyClientSideFilters = useCallback((sessions, options = {}) => (
    applyInboxSessionFilters(sessions, {
      selectedFilters,
      hideSingleMessage,
      ...options
    })
  ), [selectedFilters, hideSingleMessage]);

  const refreshPriorityCounts = useCallback(async () => {
    if (!workspaceId) {
      setPriorityCounts(DEFAULT_PRIORITY_COUNTS);
      return;
    }

    const workspaceIdNumber = parseInt(workspaceId, 10);
    if (Number.isNaN(workspaceIdNumber)) {
      setPriorityCounts(DEFAULT_PRIORITY_COUNTS);
      return;
    }

    let apiSortField = sortField;
    if (sortField === 'message_count') {
      apiSortField = 'count(messages)';
    }

    const filterConditions = buildSessionFilterConditions(workspaceIdNumber, { hideSingleMessage });
    const collectedSessions = [];
    const seenSessionIds = new Set();

    try {
      for (let page = 1; page <= MAX_INBOX_SCAN_PAGES; page += 1) {
        const response = await directusAPI.get(DIRECTUS_CHAT_SESSIONS_ENDPOINT, {
          params: {
            sort: `${sortDirection === 'asc' ? '' : '-'}${apiSortField}`,
            page,
            limit: INBOX_ITEMS_PER_PAGE,
            fields: [
              'id', 'priority_band', 'business_risk', 'count(messages)', 'messages_count',
              'overall_sentiment', 'overall_sentiment_labels', 'primary_intent', 'intent_fulfilled'
            ],
            filter: { _and: filterConditions },
            meta: 'filter_count'
          }
        });

        const formattedPageSessions = await mapRawSessions(response.data.data || []);
        formattedPageSessions.forEach((session) => {
          if (!seenSessionIds.has(session.id)) {
            seenSessionIds.add(session.id);
            collectedSessions.push(session);
          }
        });

        const filterCount = response.data.meta?.filter_count || 0;
        if (page * INBOX_ITEMS_PER_PAGE >= filterCount) break;
      }

      setPriorityCounts(
        computePriorityCounts(
          applyClientSideFilters(collectedSessions, { includePriorityFilter: false }),
          priorityFilterContext
        )
      );
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Error fetching priority counts:', error);
    }
  }, [workspaceId, sortField, sortDirection, hideSingleMessage, applyClientSideFilters, priorityFilterContext]);

  const fetchSessionsList = useCallback(async (arg = {}) => {
    const { resetPage, page: explicitPage } = normalizeFetchOptions(arg);
    const pageToFetch = explicitPage ?? (resetPage ? 1 : currentPageRef.current);
    const requestSeq = ++listRequestSeqRef.current;
    const hasExistingSessions = chatSessions.length > 0;

    if (resetPage && currentPageRef.current !== 1) {
      setCurrentPage(1);
      currentPageRef.current = 1;
    }

    if (hasExistingSessions) {
      setIsInboxRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      if (!workspaceId) {
        setChatSessions([]);
        setTotalCount(0);
        return;
      }

      const workspaceIdNumber = parseInt(workspaceId, 10);
      if (Number.isNaN(workspaceIdNumber)) {
        setChatSessions([]);
        setTotalCount(0);
        return;
      }

      let apiSortField = sortField;
      if (sortField === 'message_count') {
        apiSortField = 'count(messages)';
      }

      const response = await directusAPI.get(DIRECTUS_CHAT_SESSIONS_ENDPOINT, {
        params: {
          sort: `${sortDirection === 'asc' ? '' : '-'}${apiSortField}`,
          page: pageToFetch,
          limit: INBOX_ITEMS_PER_PAGE,
          fields: [
            'id', 'title', 'date_created', 'date_updated', 'linked_workspace',
            'overall_sentiment', 'overall_sentiment_labels', 'overall_sentiment_topics',
            'user_created.first_name', 'user_created.last_name', 'user_created.email',
            'primary_intent', 'intent_fulfilled', 'priority_band', 'effort_score',
            'no_progress_loop', 'delta_open_to_close', 'count(messages)', 'messages_count'
          ],
          filter: { _and: buildSessionFilterConditions(workspaceIdNumber, { hideSingleMessage }) },
          meta: 'total_count,filter_count'
        }
      });

      if (requestSeq !== listRequestSeqRef.current) return;

      const apiFilteredCount = response.data.meta?.filter_count
        || response.data.meta?.total_count
        || response.data.data?.length
        || 0;
      const formattedPageSessions = await mapRawSessions(response.data.data || []);
      const visibleSessions = applyClientSideFilters(formattedPageSessions, { hideSingleMessage: false });

      if (requestSeq !== listRequestSeqRef.current) return;

      setChatSessions(visibleSessions);
      setTotalCount(apiFilteredCount);

      if (!selectedSessionIdRef.current && visibleSessions.length > 0 && !urlSessionIdRef.current) {
        const firstSession = visibleSessions[0];
        setSelectedSessionId(firstSession.id);
        selectedSessionIdRef.current = firstSession.id;
        updateURL(firstSession.id);
        loadSpecificSession(firstSession.id, { isInitial: bootstrapInProgressRef.current });
      }
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Error fetching chat sessions:', error);
      if (requestSeq === listRequestSeqRef.current) {
        setChatSessions([]);
        setTotalCount(0);
      }
    } finally {
      if (requestSeq === listRequestSeqRef.current) {
        setLoading(false);
        setIsInboxRefreshing(false);
      }
    }
  }, [sortField, sortDirection, workspaceId, hideSingleMessage, applyClientSideFilters, loadSpecificSession, updateURL, selectedSessionIdRef, chatSessions.length]);

  fetchSessionsListRef.current = fetchSessionsList;

  useEffect(() => {
    if (!workspaceId || !token) return;

    if (bootstrappedWorkspaceRef.current === workspaceId) {
      setInitialLoading(false);
      return;
    }

    let cancelled = false;
    bootstrappedWorkspaceRef.current = workspaceId;
    bootstrapInProgressRef.current = true;
    setInitialLoading(true);

    bootstrapSessionIdRef.current = urlSessionIdRef.current && urlSessionIdRef.current !== 'new'
      ? urlSessionIdRef.current
      : null;

    if (bootstrapSessionIdRef.current) {
      setSelectedSessionId(bootstrapSessionIdRef.current);
      selectedSessionIdRef.current = bootstrapSessionIdRef.current;
    }

    const bootstrap = async () => {
      try {
        await fetchSessionsListRef.current({ resetPage: true });
        if (!cancelled) {
          await refreshPriorityCounts();
        }
      } finally {
        if (!cancelled) {
          bootstrapInProgressRef.current = false;
          setInitialLoading(false);
        }
      }

      if (cancelled) return;

      const sessionToLoad = bootstrapSessionIdRef.current || selectedSessionIdRef.current;
      if (sessionToLoad) {
        loadSpecificSession(sessionToLoad, { isInitial: true });
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
      bootstrapInProgressRef.current = false;
      bootstrappedWorkspaceRef.current = null;
    };
  }, [workspaceId, token, loadSpecificSession, fetchSessionsListRef, selectedSessionIdRef, refreshPriorityCounts]);

  useEffect(() => {
    if (!bootstrappedWorkspaceRef.current || bootstrapInProgressRef.current || initialLoading) return;
    if (!urlSessionId || urlSessionId === 'new') return;
    if (urlSessionId === loadedSessionIdRef.current) return;
    if (urlSessionId === bootstrapSessionIdRef.current && loadedSessionIdRef.current === urlSessionId) return;

    setSelectedSessionId(urlSessionId);
    selectedSessionIdRef.current = urlSessionId;
    loadSpecificSession(urlSessionId);
  }, [urlSessionId, loadSpecificSession, initialLoading, loadedSessionIdRef, selectedSessionIdRef]);

  useEffect(() => {
    if (!bootstrappedWorkspaceRef.current || !workspaceId || !token) return;

    const snapshot = JSON.stringify({
      sortField,
      sortDirection,
      selectedFilters,
      hideSingleMessage
    });

    if (filtersSnapshotRef.current === null) {
      filtersSnapshotRef.current = snapshot;
      return;
    }

    if (filtersSnapshotRef.current === snapshot) return;
    filtersSnapshotRef.current = snapshot;

    const timeoutId = setTimeout(() => {
      fetchSessionsListRef.current({ resetPage: true });
      refreshPriorityCounts();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [sortField, sortDirection, selectedFilters, hideSingleMessage, workspaceId, token, fetchSessionsListRef, refreshPriorityCounts]);

  useEffect(() => {
    if (!token) {
      setInitialLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!workspaceId && token) {
      setInitialLoading(false);
    }
  }, [workspaceId, token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortDropdown && sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortDropdown]);

  const handleSortChange = useCallback((sortOption) => {
    let field = sortOption.value;
    let direction = sortOption.direction;

    if (sortOption.value === 'message_count_asc') {
      field = 'message_count';
      direction = 'asc';
    }

    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setShowSortDropdown(false);
  }, []);

  const getCurrentSortOption = useCallback(() => {
    if (sortField === 'message_count' && sortDirection === 'asc') {
      return sortOptions.find((option) => option.value === 'message_count_asc');
    }
    return sortOptions.find((option) => option.value === sortField && option.direction === sortDirection)
      || sortOptions[0];
  }, [sortField, sortDirection, sortOptions]);

  const handlePageChange = useCallback((page) => {
    const nextPage = Math.min(Math.max(1, page), totalPages);
    if (nextPage === currentPageRef.current) return;
    currentPageRef.current = nextPage;
    setCurrentPage(nextPage);
    fetchSessionsList({ page: nextPage });
  }, [totalPages, fetchSessionsList]);

  const handleRowClick = useCallback((session) => {
    if (session.id === loadedSessionIdRef.current) {
      if (selectedSessionIdRef.current !== session.id) {
        setSelectedSessionId(session.id);
        selectedSessionIdRef.current = session.id;
        updateURL(session.id);
      }
      return;
    }

    setSelectedSessionId(session.id);
    selectedSessionIdRef.current = session.id;
    updateURL(session.id);
    loadSpecificSession(session.id);
  }, [updateURL, loadSpecificSession, loadedSessionIdRef, selectedSessionIdRef]);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters(EMPTY_INBOX_SELECTED_FILTERS);
  }, []);

  const handleArchiveSession = useCallback(async (sessionId) => {
    if (!sessionId) return;

    try {
      await directusAPI.patch(`/items/chat_sessions/${sessionId}`, {
        status: 'archived'
      });

      setChatSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setTotalCount((prev) => Math.max(0, prev - 1));

      return sessionId;
    } catch (error) {
      process.env.NODE_ENV === 'development' && console.error('Error archiving session:', error);
      throw error;
    }
  }, []);

  return {
    chatSessions,
    setChatSessions,
    selectedSessionId,
    setSelectedSessionId,
    selectedSessionIdRef,
    totalCount,
    setTotalCount,
    currentPage,
    totalPages,
    itemsPerPage: INBOX_ITEMS_PER_PAGE,
    loading,
    initialLoading,
    isInboxRefreshing,
    hideSingleMessage,
    setHideSingleMessage,
    selectedFilters,
    setSelectedFilters,
    priorityCounts,
    visiblePriorityFilters,
    priorityFilterOptions,
    sortOptions,
    sortField,
    sortDirection,
    showSortDropdown,
    setShowSortDropdown,
    sortDropdownRef,
    handleSortChange,
    getCurrentSortOption,
    handlePageChange,
    handleRowClick,
    handleClearFilters,
    handleArchiveSession,
    fetchSessionsListRef,
    bootstrappedWorkspaceRef
  };
};

export default useAssistantInbox;
